/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Euro, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { URGENCY_OPTIONS } from './StepSlotPicker';

/**
 * Step 2 of the BookingWizard: read-only summary before confirming.
 *
 * Sprint 10: also renders the "Per chi" row and any active family-bundle
 * discount so the patient sees the final price before proceeding to payment.
 */
const StepReview = ({ doctor, selectedSlot, urgency, isFree, familyData, familyMemberKey }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';

  if (!selectedSlot) return null;
  const urgencyOpt = URGENCY_OPTIONS.find((o) => o.value === urgency) || URGENCY_OPTIONS[0];

  // ---- Family bundle context ----
  const hasFamily = !!familyData?.has_family;
  const familyDiscountPct = hasFamily ? Number(familyData?.discount_pct || 0) : 0;
  const applyFamilyDiscount = !isFree && familyDiscountPct > 0;

  // ---- Per chi row ----
  let familyRow = null;
  if (hasFamily && (familyData.members || []).length > 0) {
    const selected = familyMemberKey
      ? (familyData.members || []).find((m) => m.key === familyMemberKey)
      : null;
    const label = selected
      ? `${selected.full_name}${selected.relation ? ` · ${selected.relation}` : ''}`
      : (familyData.self?.full_name || 'Io');
    familyRow = (
      <div className="flex justify-between gap-4" data-testid="review-family-row">
        <dt className="text-muted-foreground">Per chi</dt>
        <dd className="font-semibold text-right">{label}</dd>
      </div>
    );
  }

  // ---- Price display ----
  const basePrice = Number(doctor.visit_price_eur || 0);
  const finalPrice = applyFamilyDiscount
    ? Math.round(basePrice * (100 - familyDiscountPct)) / 100
    : basePrice;
  const savings = Math.round((basePrice - finalPrice) * 100) / 100;

  const priceCell = () => {
    if (isFree) {
      return <dd className="font-bold text-right text-primary">{t('telemedFreeTag')}</dd>;
    }
    if (applyFamilyDiscount) {
      return (
        <dd className="text-right" data-testid="review-price-discounted">
          <div className="flex items-baseline justify-end gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground line-through" data-testid="review-price-base">
              € {basePrice.toFixed(2)}
            </span>
            <span className="font-bold text-primary text-base" data-testid="review-price-final">
              € {finalPrice.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <Badge variant="success" className="text-[10px] gap-1" data-testid="review-discount-badge">
              <Sparkles className="h-3 w-3" /> -{familyDiscountPct}% Bundle
            </Badge>
          </div>
        </dd>
      );
    }
    return (
      <dd className="font-bold text-right text-primary">
        € {basePrice.toFixed(2)}
      </dd>
    );
  };

  return (
    <div className="space-y-4 animate-fade-slide">
      <div className="rounded-2xl border border-border/60 p-4 bg-card">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          {t('bookSummaryTitle')}
        </div>
        <dl className="text-sm space-y-2">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t('bookSummaryDoctor')}</dt>
            <dd className="font-semibold text-right">Dr. {doctor.full_name}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t('bookSummaryDateTime')}</dt>
            <dd className="font-semibold text-right">
              {new Date(selectedSlot).toLocaleString(locale, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{t('bookSummaryUrgency')}</dt>
            <dd className="text-right">
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                  urgencyOpt.cls,
                )}
              >
                {t(urgencyOpt.labelKey)}
              </span>
            </dd>
          </div>
          <div className="flex justify-between gap-4 items-start">
            <dt className="text-muted-foreground pt-1">{t('bookSummaryPrice')}</dt>
            {priceCell()}
          </div>
          {familyRow}
        </dl>
      </div>

      {applyFamilyDiscount && (
        <div className="rounded-2xl border border-accent/40 bg-accent/10 p-3 flex items-start gap-2" data-testid="review-savings-note">
          <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <span className="font-semibold">Risparmi € {savings.toFixed(2)}</span>
            <span className="text-muted-foreground"> grazie al tuo bundle {familyData?.bundle_label || 'Famiglia'}.</span>
          </div>
        </div>
      )}

      {!isFree && (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 flex items-start gap-2">
          <Euro className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs text-muted-foreground">{t('bookPaymentNote')}</div>
        </div>
      )}
    </div>
  );
};

export default StepReview;
